<template>
  <div
    class="media-column"
    :class="{
      sticky: !isMobile,
    }"
  >
    <div
      class="main-image-and-alts-container"
      :class="{
        'overflow-gutters-row': isMobile,
        square: isBcom && isHomeCategory,
      }"
    >
      <AltImageSelector
        v-if="!isMobile && hasCarouselImages"
        class="vertical-alternate"
        aria-label="alternate images"
        :orientation="orientation"
        :carousel-images="carouselImages"
        :current-page="currentPage"
        :current-page-on-hover="currentPageOnHover"
        :seo-alt="seoAlt"
        @image-change="updateCurrentPage"
        @image-change-on-hover="updateCurrentPageOnHover"
      />

      <MainImageContainer :show-indicator="isMobile" :has-carousel-images="hasCarouselImages" />
    </div>

    <ProductDetailsAiPrompts v-if="isGoogleAiUnderImageEnabled" class="margin-top-m" ask-button-on-next-row is-switch-button-available />
  </div>
</template>

<script setup>
import { computed, toValue, provide } from 'vue';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { getSeoAlt } from '~/utils/getSeoAlt';
import useConsolidatedProductDetails from '~/composables/useConsolidatedProductDetails';
import { useImageryStateProvide } from '~/composables/useImageryState';
import useImages from '~/composables/images/useImages';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useBrand } from '~/composables/useBrand';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';
import { useTraits } from '~/composables/useTraits';
import { tagAltImagesClicked, tagLastAltImagesClicked } from '~/features/analytics/image';
import { useGoogleAiEligibility } from '~/composables/useGoogleAiEligibility';
import ProductDetailsAiPrompts from '~/modules/productDetails/components/ProductDetailsAiPrompts';
import once from '~/utils/once';
import { ALT_IMAGE_ORIENTATION } from '../../../constants/common';

import MainImageContainer from './MainImageContainer';
import AltImageSelector from './AltImageSelector';

const { isGoogleAiUnderImageEnabled } = useGoogleAiEligibility();

const triggerAltImageClickOnce = once(tagAltImagesClicked);
provide('triggerAltImageClickOnce', triggerAltImageClickOnce);

const triggerLastAltImageClickOnce = once(tagLastAltImagesClicked);
provide('triggerLastAltImageClickOnce', triggerLastAltImageClickOnce);

const { productName, brandName } = useCommonProductGetters();
const { productName: consolidatedName } = useConsolidatedProductDetails();
const { selectedColor } = useTraits();
const { isBcom } = useBrand();

const seoAlt = computed(() => getSeoAlt(toValue(consolidatedName) || toValue(productName), toValue(brandName), selectedColor.value?.name));
const { carouselImages } = useImages();
const hasCarouselImages = computed(() => carouselImages.value?.length > 1);
const { isMobile, isTablet } = useDeviceType();
const orientation = computed(() => (isTablet.value ? ALT_IMAGE_ORIENTATION.horizontal : ALT_IMAGE_ORIENTATION.vertical));
const { isHomeCategory } = useTopLevelCategoryFlags();

const { currentPage, currentPageOnHover, updateCurrentPage, updateCurrentPageOnHover } = useImageryStateProvide();
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.media-column {
  width: 100%;

  &.sticky {
    @include position-sticky(10);
  }
}

.main-image-and-alts-container {
  display: flex;
  width: 100%;
  overflow: hidden;
  touch-action: pan-y;
  flex-direction: column-reverse;

  @include breakpoint(large) {
    flex-direction: row;
  }

  &.overflow-gutters-row {
    --gap: calc(#{map-get($grid-container-padding, 'small')} / 2);
    position: relative;
    overflow: visible;
    width: calc(100% + var(--gap) * 2);
    left: calc(-1 * var(--gap));
    right: var(--gap);
    overflow: visible;
  }

  &.square {
    :deep() {
      .picture-image,
      .video-container {
        aspect-ratio: 1/1;
        object-fit: contain;
        @include breakpoint(medium up) {
          height: 100%;
        }
      }
    }
  }

  :deep() {
    .mobile-entries-cta {
      .product-images-cta-container {
        @include breakpoint(small only) {
          position: static;
          display: flex;
          justify-content: center;
        }
      }
    }
    .picture-container {
      width: 100%;
      height: auto;
    }
    .picture-image,
    .video-container,
    .ar-indicator-wrapper {
      aspect-ratio: 4/5;
      object-fit: cover;
      @include breakpoint(medium up) {
        height: 100%;
      }
    }
  }
}

.vertical-alternate {
  min-width: 100%;
  width: 100%;
  display: none;

  @include breakpoint(medium up) {
    display: block;
    min-width: 106px;
    width: 106px;
  }
  @include breakpoint(large down) {
    min-width: 68px;
    width: 68px;
  }
  @include breakpoint(medium down) {
    min-width: 100%;
    width: 100%;
  }
  :deep() {
    .picture-container {
      width: 100%;
      height: auto;

      .picture-image {
        height: 100%;
        object-fit: contain;
      }
    }
  }
}
</style>
